// Import from libraries
import { type UseSuspenseQueryResult } from "@tanstack/react-query";
import { useState, type TdHTMLAttributes } from "react";
import { useRouteContext, useLoaderData } from "@tanstack/react-router";
import TableDateCell from "@/components/shared/tables/TableDateCell";
import { BsDownload } from "react-icons/bs";
import { joinClasses } from "@/utils/styling";

import {
  type GridRowClickEvent,
  type GridColumnProps,
} from "@progress/kendo-react-grid";

// Import from scripts
import { fetchAuthenticated } from "@/queries/shared";
import {
  FileStatus,
  type FileSubmission,
  type FileSubmissionUI,
} from "@/models/file-submission.tsx";
import { useAuthenticatedSuspenseQuery } from "@/queries/shared";
import { getTableColumnSelectOptions } from "@/utils/tables";

// Import components
import { DownloadBlob } from "@/components/components";
import Card from "@/components/shared/Card";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import FileStatusPopup from "@/components/match-tool/FileStatusPopup";
import TooltipTrigger from "@/components/shared/TooltipTrigger";
import CategoryFilterCell from "@/components/shared/tables/CategoryFilterCell";
import {
  NumericFilterCell,
  DateFilterCell,
} from "@/components/shared/tables/GridFilterCells";
import Icon from "@/components/shared/Icon";
import { stripAriaSelected } from "@/components/shared/tables/KendoTableClientState/kendo-cell-utils";

const REFETCH_INTERVAL = 10000; // 10 seconds

function getFileStatusName(statusCode: number): string {
  return FileStatus.get(statusCode.toString())?.Text ?? "--";
}

const statusTextColor: Record<string, string> = {
  Posted: "var(--status-success)",
  Failed: "var(--status-error)",
  Rejected: "var(--status-error)",
  Pending: "var(--status-warning)",
  Processing: "var(--status-warning)",
};

export default function SubmittedFilesCard() {
  const currentUserOrg = useRouteContext({ from: "/$leaid" }).currentUserOrg;
  const path = `/json/candidatesubmissions/${currentUserOrg.esiIdPath}`;
  const {
    data,
    error,
  }: UseSuspenseQueryResult<{ records: FileSubmission[] }, Error> =
    useAuthenticatedSuspenseQuery(
      path,
      "candidatesubmissions",
      REFETCH_INTERVAL
    );

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileSubmission | null>(null);

  const files = data.records ?? [];

  // Add StatusName field based on CurrentStatus field
  const tableData: FileSubmissionUI[] = files.map((file) => ({
    ...file,
    StatusName: getFileStatusName(file.CurrentStatus),
  }));

  const statusOptions = getTableColumnSelectOptions(tableData, "StatusName");
  const createdByOptions = getTableColumnSelectOptions(
    tableData,
    "CreatedByUserId"
  );

  const columns: GridColumnProps[] = [
    {
      id: "ClientFileName",
      field: "ClientFileName",
      title: "Filename",
      width: 250,
    },
    {
      id: "CreateDate",
      field: "CreateDate",
      title: "Date Submitted",
      filter: "date",
      cells: {
        data: TableDateCell,
        filterCell: (props) => DateFilterCell(props, "Date Submitted"),
      },
    },
    {
      id: "CreatedByUserId",
      field: "CreatedByUserId",
      title: "Submitted By",
      cells: {
        filterCell: (props) => CategoryFilterCell(props, [...createdByOptions]),
      },
      width: 250,
    },
    {
      id: "Errors",
      field: "Errors",
      title: "# of Errors",
      filter: "numeric",
      cells: {
        filterCell: (props) => NumericFilterCell(props, "# of Errors"),
      },
    },
    {
      id: "InputRecords",
      field: "InputRecords",
      title: "# of Records",
      filter: "numeric",
      cells: {
        filterCell: (props) => NumericFilterCell(props, "# of Records"),
      },
    },
    {
      id: "StatusName",
      field: "StatusName",
      title: "Status",
      cells: {
        data: ({ dataItem, tdProps }) => (
          <SubmissionStatusCell
            submission={dataItem as FileSubmissionUI}
            tdProps={tdProps}
          />
        ),
        filterCell: (props) => CategoryFilterCell(props, [...statusOptions]),
      },
    },
  ];

  const handleKendoRowClick = (event: GridRowClickEvent) => {
    const file: FileSubmissionUI = event.dataItem as FileSubmissionUI,
      status = file.StatusName;

    // Only open popup for Posted, Failed, and Rejected files (not pending)
    if (status === "Posted" || status === "Failed" || status === "Rejected") {
      setSelectedFile(file);
      setModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setSelectedFile(null);
    setModalOpen(false);
  };

  return (
    <>
      <Card className="mb-3 no-padding card-border">
        <div
          className="text-white p-3 d-flex align-items-center gap-2"
          style={{
            background: "var(--color-p3)",
            fontWeight: 500,
            borderTopLeftRadius: "inherit",
            borderTopRightRadius: "inherit",
          }}
        >
          <span>Submitted Files</span>
        </div>

        <KendoTableClientState
          data={tableData}
          columns={columns}
          error={error}
          initialSort={[{ field: "CreateDate", dir: "desc" }]}
          onRowClick={handleKendoRowClick}
        />
      </Card>

      {selectedFile && (
        <FileStatusPopup
          show={modalOpen}
          onClose={handleModalClose}
          file={selectedFile}
        />
      )}
    </>
  );
}

function SubmissionStatusCell({
  submission,
  tdProps,
}: {
  submission: FileSubmissionUI;
  tdProps?: TdHTMLAttributes<HTMLTableCellElement> | null;
}) {
  const cleanTdProps = stripAriaSelected(tdProps);
  const globalState = useLoaderData({ from: "__root__" });
  const currentUserOrg = useRouteContext({ from: "/$leaid" }).currentUserOrg;
  const statusText =
    FileStatus.get(submission.CurrentStatus.toString())?.Text ?? "--";
  const statusDef = FileStatus.get(submission.CurrentStatus.toString());
  const color = statusTextColor[statusText] ?? "var(--status-default)";

  const downloadExtract = async (candidateSubmissionId: string) => {
    const extractData = await fetchAuthenticated(
      globalState,
      `/json/candidatesubmission/${currentUserOrg.esiIdPath}/${candidateSubmissionId}/extract`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );
    // ✅ Guard against error responses — without this check a 4xx/5xx would
    // silently download a blob of error HTML instead of the actual file.
    if (!extractData.ok) {
      throw new Error(`Download failed: ${extractData.status}`);
    }
    const serverFilename =
      extractData.headers["Content-Disposition"]?.split("filename=")[1];
    DownloadBlob(
      await extractData.blob(),
      serverFilename || "candidate_results.csv"
    );
  };

  const label = (
    <span
      className="d-flex align-items-center gap-1"
      style={{ color, whiteSpace: "nowrap" }}
    >
      {statusDef?.Icon && <Icon name={statusDef.Icon} />}
      {statusText}
    </span>
  );

  if (statusText === "Posted" && submission.Errors === 0) {
    return (
      <td
        {...cleanTdProps}
        className={joinClasses(
          "d-flex align-items-center gap-3",
          cleanTdProps?.className
        )}
      >
        {label}
        <button
          type="button"
          className="btn btn-link p-0"
          title="Download results"
          aria-label={`Download results for ${submission.ClientFileName ?? "file"}`}
          style={{ color: "#0f172a", lineHeight: 1 }}
          onClick={(e) => {
            e.stopPropagation();
            downloadExtract(submission.Id).catch((err) =>
              console.error("Error downloading extract:", err)
            );
          }}
        >
          <BsDownload size={18} aria-hidden="true" />
        </button>
      </td>
    );
  }

  if (statusText === "Rejected") {
    return (
      <td {...cleanTdProps}>
        <TooltipTrigger tooltipText={`${submission.Errors ?? 0} errors`}>
          {label}
        </TooltipTrigger>
      </td>
    );
  }

  return <td {...cleanTdProps}>{label}</td>;
}
